import React, { useState, useRef } from "react";
import { FlexColumn, FlexRow } from "@/ui/FlexContainer/FlexContainer";
import Typography from "@/ui/Typography/Typography";
import Modal from "@/ui/Modal/Modal";
import Button from "@/ui/Button/Button";
import { withdrawEarnings } from "@/api/base";
import { dataTrigger } from "@/analytics";
import { useEarningsDataContext } from "@/store/EarningsProvider";
import { useUserInfoStore } from "@/store/userInfoStore";
import Confetti, { ConfettiHandle } from "@/components/Confetti/Confetti";
import { useRampHaptic } from "@/utils/hooks/useRampHaptic";
import { useConfettiShow } from "@/utils/hooks/useConfettiShow";
import { CloseBtn } from "@/components/CloseBtn/CloseBtn";
import cls from "./styles.module.scss";

export const Balance = () => {
  const [isOpen, setIsOpen] = useState<boolean>(false);

  const yandexId = useUserInfoStore((s) => s.yandexId);

  const { earnings } = useEarningsDataContext();
  const [isWithdrawLoading, setIsWithdrawLoading] = useState(false);
  const [isWithdraw, setIsWithdraw] = useState(false);

  const confettiRef = useRef<ConfettiHandle | null>(null);
  const { ramp } = useRampHaptic();
  const { fireworkParty, fireFromCorners } = useConfettiShow(confettiRef);

  const onOpenConfetti = () => {
    setTimeout(() => {
      fireFromCorners();
      ramp(25, 15);
    }, 750);
    setTimeout(() => {
      fireworkParty(8, 250);
    }, 1350);
  };

  const onModalOpen = async () => {
    setIsOpen(true);
  };

  const onClickWithdrawEarnings = async () => {
    if (isWithdrawLoading) return;

    try {
      setIsWithdrawLoading(true);

      await withdrawEarnings(yandexId);
      onOpenConfetti();
      setIsWithdraw(true);
    } catch (e) {
      console.log(e);
    } finally {
      setIsWithdrawLoading(false);
    }
  };

  return (
    <>
      <Modal isOpen={isOpen} onClose={() => setIsOpen(false)}>
        <Confetti ref={confettiRef} />

        <FlexColumn
          gap={24}
          alignItems="stretch"
          justifyContent="center"
          className={cls.popupBlock}
        >
          <CloseBtn onclose={() => setIsOpen(false)} trigger="ref.balance.close" />

          {!isWithdraw ? (
            <FlexColumn gap={20} className={cls.items}>
              <Typography variant="h3">Баланс</Typography>

              <FlexColumn gap={0}>
                <FlexRow justifyContent="space-between" className={cls.item}>
                  <Typography className={cls.modalLabel}>
                    Всего заработано
                  </Typography>
                  <Typography className={cls.currentEarned}>
                    {earnings.total.earned.toLocaleString("ru-RU")} ₽
                  </Typography>
                </FlexRow>

                <FlexRow justifyContent="space-between" className={cls.item}>
                  <FlexColumn gap={0}>
                    <Typography className={cls.modalLabel}>
                      Ещё можно заработать
                      <br />
                      за текущих рефералов
                    </Typography>
                  </FlexColumn>
                  <Typography className={cls.currentEarned}>
                    {(
                      earnings.active.total_possible - earnings.active.earned
                    ).toLocaleString("ru-RU")}{" "}
                    ₽
                  </Typography>
                </FlexRow>

                <FlexRow justifyContent="space-between" className={cls.item}>
                  <Typography className={cls.modalLabel}>
                    Доступно к выводу
                  </Typography>
                  <Typography
                    className={
                      earnings.balance > 0
                        ? cls.currentBalance
                        : cls.zeroBalance
                    }
                  >
                    {earnings.balance.toLocaleString("ru-RU")} ₽
                  </Typography>
                </FlexRow>
              </FlexColumn>

              <FlexColumn gap={10} className={cls.btn}>
                <Button
                  trigger="ref.balance.invite_more"
                  label="Пригласить ещё"
                  onClick={() => {
                    setIsOpen(false);
                  }}
                  animation
                />
                <Button
                  trigger="ref.balance.withdraw"
                  variant="text"
                  label="Вывести"
                  loading={isWithdrawLoading}
                  disabled={!(earnings.balance > 0)}
                  onClick={onClickWithdrawEarnings}
                />
              </FlexColumn>
            </FlexColumn>
          ) : (
            <FlexColumn
              gap={20}
              alignItems="stretch"
              className={cls.withdrawSucces}
            >
              <div className={cls.withdrawSuccessIcon}>✅</div>

              <FlexColumn gap={8}>
                <Typography variant="h3">Заявка оформлена!</Typography>
                <Typography className={cls.withdrawSuccessDesc}>
                  Подробнее читайте в сообщении от бота
                </Typography>
              </FlexColumn>

              <Typography className={cls.withdrawSuccessBalance}>
                {earnings.balance.toLocaleString("ru-RU")} ₽
              </Typography>
              <Button
                trigger="ref.balance.success_close"
                label="Закрыть"
                variant="white"
                onClick={() => setIsOpen(false)}
              />
            </FlexColumn>
          )}
        </FlexColumn>
      </Modal>

      <FlexRow
        gap={8}
        className={cls.balance}
        onClick={onModalOpen}
        role="button"
        {...dataTrigger("ref.main.balance")}
      >
        <FlexRow gap={6}>
          <div className={cls.ruble}>₽</div>
          <Typography variant="body2">
            {earnings.balance.toLocaleString("ru-RU")} ₽
          </Typography>
        </FlexRow>
      </FlexRow>
    </>
  );
};
